<template>
  <div>
    <request-review-popup
      :model-value="showReviewModal"
      :sitter-id="sitterId"
      :sitter-name="sitterName"
      @close="() => (showReviewModal = false)"
    />
    <div class="flex flex-wrap gap-12">
      <div class="max-w-max md:max-w-[65%]">
        <p class="text-h4 mb-16 inline-flex items-center gap-8 flex-wrap">
          {{ headerText }}
          <badge
            v-if="isEligibilityReview"
            badge-content="Under Review"
            variant="urgent"
            icon="time-outline"
            class="align-middle"
          />
        </p>
        <p class="text-body w-full">
          {{ subtext }}
          <a
            v-if="COPY.learnMoreLabel"
            href="https://family.sittercity.com/trusted-caregiver/"
            target="_blank"
            rel="noopener noreferrer"
            class="underline"
          >
            {{ COPY.learnMoreLabel }}
          </a>
        </p>
      </div>
      <div class="min-w-[146px] max-w-max m-auto md:mr-16">
        <completeness-meter :completeness-percentage="completenessScore" />
      </div>
    </div>
    <div class="mt-24">
      <div v-if="showsEligibilityTips">
        <p v-if="isComplete" class="text-h4 mb-16">{{ COPY.tipsHeader }}</p>
        <div class="flex flex-col md:flex-row md:gap-16">
          <ul class="list-disc w-full md:w-50">
            <li v-for="(tip, index) in leftColumnTips" :key="tip.id" class="ml-16" :class="{ 'mt-16': index > 0 }">
              <a
                v-if="tipHref(tip)"
                :href="tipHref(tip)"
                class="underline"
                :class="{ 'cursor-pointer': tip.action === 'review' }"
                @click="onTipClick($event, tip)"
              >
                {{ tip.label }}
              </a>
              <template v-else>{{ tip.label }}</template>
            </li>
          </ul>
          <ul class="list-disc w-full md:w-50 mt-16 md:mt-0">
            <li v-for="(tip, index) in rightColumnTips" :key="tip.id" class="ml-16" :class="{ 'mt-16': index > 0 }">
              <a
                v-if="tipHref(tip)"
                :href="tipHref(tip)"
                class="underline"
                :class="{ 'cursor-pointer': tip.action === 'review' }"
                @click="onTipClick($event, tip)"
              >
                {{ tip.label }}
              </a>
              <template v-else>{{ tip.label }}</template>
            </li>
          </ul>
        </div>
      </div>
      <step-card-list v-else :items="steps" :collapsible-filter="(item) => item.isCollapsible" />
    </div>
    <profile-confetti :show-confetti="showConfetti" />
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useStore } from 'vuex';
import CompletenessMeter from '@/components/completeness_meter/CompletenessMeter.vue';
import StepCardList from '@/components/step_card_list/StepCardList.vue';
import Badge from '@/components/badge/Badge.vue';
import RequestReviewPopup from '../RequestReviewPopup.vue';
import ProfileConfetti from '../profile_progress/ProfileConfetti.vue';
import {
  CHECKLIST_ITEM_COPY,
  CHECKLIST_ITEM_ORDER,
  COMPLETE_TIPS,
  CONFETTI_SESSION_KEY,
  COPY,
  computeCompletenessScore,
  type CompleteTip,
  type TrustedCaregiver,
  type TrustedCaregiverChecklistItemType,
} from './trustedCaregiverProgressCopy';

defineOptions({ name: 'TrustedCaregiverProgress' });

const props = withDefaults(
  defineProps<{
    trustedCaregiver: TrustedCaregiver;
    careType?: string;
  }>(),
  {
    careType: 'babysitter',
  }
);

const store = useStore();
const showReviewModal = ref(false);
const showConfetti = ref(false);

const account = computed(() => store.getters['account/getAccount']);
const sitterId = computed(() => account.value?.id ?? '');
const sitterName = computed(() => {
  const firstName = account.value?.firstName ?? '';
  const lastInitial = account.value?.lastName ? account.value.lastName[0] : '';
  return `${firstName} ${lastInitial}.`.trim();
});

const isOnboardingReview = computed(() => props.trustedCaregiver.onboardingStatus === 'UNDER_REVIEW');
const isEligibilityReview = computed(() => props.trustedCaregiver.onboardingStatus === 'ELIGIBILITY_REVIEW');
const isComplete = computed(() => props.trustedCaregiver.onboardingStatus === 'COMPLETE');
const showsEligibilityTips = computed(() => isComplete.value || isEligibilityReview.value);

const completenessScore = computed(() => computeCompletenessScore(props.trustedCaregiver.checklist));

const headerText = computed(() => {
  if (isComplete.value) return COPY.completeHeader;
  if (isEligibilityReview.value) return COPY.eligibilityHeader;
  if (isOnboardingReview.value) return COPY.underReviewHeader;
  return COPY.inProgressHeader;
});

const subtext = computed(() => {
  if (isComplete.value) return COPY.completeSubtext;
  if (isEligibilityReview.value) return COPY.eligibilityReviewSubtext;
  if (isOnboardingReview.value) return COPY.underReviewSubtext;
  return COPY.inProgressSubtext;
});

const tipMidpoint = Math.ceil(COMPLETE_TIPS.length / 2);
const leftColumnTips = COMPLETE_TIPS.slice(0, tipMidpoint);
const rightColumnTips = COMPLETE_TIPS.slice(tipMidpoint);

const normalizedCareType = computed(() => props.careType?.toLowerCase() || 'babysitter');

const tipLinkMap = computed((): Record<NonNullable<CompleteTip['action']>, string> => {
  const careType = normalizedCareType.value;
  return {
    photo: '/sitter/photo_redirect?return_to_dashboard=true',
    review: '#',
    availability: '/availability',
    cpr: `/provider/profile/${careType}/edit/certifications`,
    firstAid: `/provider/profile/${careType}/edit/certifications`,
  };
});

function tipHref(tip: CompleteTip): string | undefined {
  if (!tip.action) return undefined;
  return tipLinkMap.value[tip.action];
}

function onTipClick(event: Event, tip: CompleteTip) {
  if (tip.action !== 'review') return;
  event.preventDefault();
  showReviewModal.value = true;
}

const stepLinkMap = computed((): Record<TrustedCaregiverChecklistItemType, string | undefined> => {
  const careType = normalizedCareType.value;
  return {
    PHOTO: '/sitter/photo_redirect?return_to_dashboard=true',
    AVAILABILITY: `/availability`,
    REVIEWS: undefined,
    VIDEO: '/account/providers/best-practices/safe-and-sound',
    CPR_CERTIFICATE: `/provider/profile/${careType}/edit/certifications`,
  };
});

function buildStep(type: TrustedCaregiverChecklistItemType, complete: boolean) {
  const copy = CHECKLIST_ITEM_COPY[type];
  const linkURL = !complete ? stepLinkMap.value[type] : undefined;

  return {
    variant: complete ? 'completedTask' : 'incompleteTask',
    title: copy.title,
    content: copy.content,
    linkURL,
    disabled: complete,
    isCollapsible: !isOnboardingReview.value && complete,
    useFullPageNavigation: !!linkURL,
    modalComponent: !complete && type === 'REVIEWS' ? RequestReviewPopup : undefined,
    modalProps: !complete && type === 'REVIEWS' ? { sitterId: sitterId.value, sitterName: sitterName.value } : undefined,
  };
}

const steps = computed(() => {
  const completionByType = new Map(props.trustedCaregiver.checklist.map((item) => [item.type, item.complete]));
  const incomplete = [];
  const completed = [];

  // Fixed display order: photo → availability → reviews → video → CPR / First Aid
  CHECKLIST_ITEM_ORDER.forEach((type) => {
    const step = buildStep(type, Boolean(completionByType.get(type)));
    if (step.variant === 'completedTask') {
      completed.push(step);
    } else {
      incomplete.push(step);
    }
  });

  return [...incomplete, ...completed];
});

watch(
  () => props.trustedCaregiver.onboardingStatus,
  (status) => {
    // Only COMPLETE (CS-activated) triggers confetti — not UNDER_REVIEW or ELIGIBILITY_REVIEW.
    if (status !== 'COMPLETE') {
      sessionStorage.removeItem(CONFETTI_SESSION_KEY);
    }
    showConfetti.value = status === 'COMPLETE' && sessionStorage.getItem(CONFETTI_SESSION_KEY) !== sitterId.value;
    if (showConfetti.value) {
      setTimeout(() => {
        showConfetti.value = false;
        sessionStorage.setItem(CONFETTI_SESSION_KEY, sitterId.value);
      }, 2500);
    }
  },
  { immediate: true }
);
</script>
